/**
 * AppointmentStatusColors.js
 * ─────────────────────────────────────────────────────────────────────────────
 * FONTE DE VERDADE CENTRALIZADA para as cores visuais dos status de agendamento.
 *
 * Todos os componentes que exibem cores de status DEVEM consumir deste arquivo:
 *   - AppointmentCard.module.css (card da agenda — classes de status)
 *   - AppointmentCard.js         (faixa de cancelado/falta — estilos inline)
 *   - Appointments.js            (legenda desktop — LEGEND_ITEMS)
 *
 * Cores da empresa (docs/features/appointment-steps): a empresa pode trocar a
 * cor de alguns status e dar cor a cada etapa, sempre escolhendo da PALETTE
 * abaixo (nunca cor livre). Sem escolha, vale a cor padrão de cada status.
 * As escolhas chegam no get_company e entram por setStatusColorOverrides();
 * getStatusColors() já devolve a cor escolhida.
 *
 * ─── Paleta de status ────────────────────────────────────────────────────────
 *
 * Hierarquia de azuis (do mais claro ao mais intenso):
 *   Confirmado          → #EEF5FF  (azul muito claro)
 *   Aguardando no local → #C8DEFF  (azul intermediário)
 *   Em atendimento      → #1D4ED8  (azul vibrante/intenso, texto branco)
 *
 * Bloqueado → cinza neutro (igual ao visual do "Blocked Time" / horário bloqueado)
 *   Fundo: #F1F3F5 + listras diagonais rgba(0,0,0,0.04) — mesmo padrão do slotBlocked
 *
 * ─── Estrutura de cada entrada ───────────────────────────────────────────────
 * {
 *   // Card (AppointmentCard.module.css) — background do card
 *   bg: string,
 *   // Card — cor do cardTime e cardProcedure (texto secundário)
 *   timeColor: string,
 *   // Faixa de cancelado/falta — cor da borda e do badge
 *   borderColor: string,
 *   // Faixa de cancelado/falta — cor das iniciais
 *   textColor: string,
 *   // Legenda (Appointments.js LEGEND_ITEMS) — bg e border do dot
 *   legendBg: string,
 *   legendBorder: string,
 *   // Opcional: backgroundImage para padrão de listras (Bloqueado)
 *   bgImage?: string,
 *   // Opcional: opacity para status "fantasma" (Reagendado)
 *   opacity?: number,
 *   // Opcional: border para status "fantasma" (Reagendado)
 *   border?: string,
 * }
 */

// Hachura suave do card de bloqueio (um pouco mais visível que a do slotBlocked,
// o Blocked Time da empresa, que usa rgba 0.04)
const BLOCK_STRIPE_PATTERN = 'repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(100,116,139,0.07) 6px, rgba(100,116,139,0.07) 8px)';

const AppointmentStatusColors = {

  // ── Agendado ────────────────────────────────────────────────────────────────
  scheduled: {
    bg:           '#F7F8FA',
    timeColor:    '#8896A8',
    borderColor:  '#3D4654',
    textColor:    '#3D4654',
    legendBg:     '#F7F8FA',
    legendBorder: '#3D4654',
  },

  // ── Reagendado — "fantasma": desbotado, claramente substituído ──────────────
  rescheduled: {
    bg:           '#F5F7FA',
    timeColor:    '#8896A8',
    borderColor:  '#A0AABB',
    textColor:    '#8896A8',
    legendBg:     '#F5F7FA',
    legendBorder: '#A0AABB',
    border:       '1px dashed #A0AABB',
    opacity:      0.55,
  },

  // ── Confirmado — azul claro (base da hierarquia de azuis) ───────────────────
  confirmed: {
    bg:           '#EEF5FF',
    timeColor:    '#6B85B8',
    borderColor:  '#2655B8',
    textColor:    '#2655B8',
    legendBg:     '#EEF5FF',
    legendBorder: '#2655B8',
  },

  // ── Confirmado pelo paciente — azul ligeiramente mais saturado ──────────────
  confirmed_by_patient: {
    bg:           '#E8F2FF',
    timeColor:    '#6278B0',
    borderColor:  '#1E47A8',
    textColor:    '#1E47A8',
    legendBg:     '#E8F2FF',
    legendBorder: '#1E47A8',
  },

  // ── Aguardando no local — azul intermediário (entre Confirmado e Em atendimento)
  waiting: {
    bg:           '#C8DEFF',
    timeColor:    '#1E4DA8',
    borderColor:  '#1E4DA8',
    textColor:    '#1E4DA8',
    legendBg:     '#C8DEFF',
    legendBorder: '#1E4DA8',
  },

  // ── Em atendimento — azul vibrante/intenso (topo da hierarquia de azuis) ────
  doing: {
    bg:           '#1D4ED8',
    timeColor:    '#BFDBFE',
    borderColor:  '#1D4ED8',
    textColor:    '#FFFFFF',
    legendBg:     '#1D4ED8',
    legendBorder: '#1D4ED8',
  },

  // ── Realizado ───────────────────────────────────────────────────────────────
  completed: {
    bg:           '#EEF9F1',
    timeColor:    '#5E9A78',
    borderColor:  '#276B47',
    textColor:    '#276B47',
    legendBg:     '#EEF9F1',
    legendBorder: '#276B47',
  },

  // ── Cancelado ───────────────────────────────────────────────────────────────
  canceled: {
    bg:           '#FAF3EC',
    timeColor:    '#A87A58',
    borderColor:  '#7A4E2A',
    textColor:    '#7A4E2A',
    legendBg:     '#FAF3EC',
    legendBorder: '#7A4E2A',
  },

  // ── Cancelado pelo paciente ─────────────────────────────────────────────────
  canceled_by_patient: {
    bg:           '#FCF6F1',
    timeColor:    '#B89070',
    borderColor:  '#8A6040',
    textColor:    '#8A6040',
    legendBg:     '#FCF6F1',
    legendBorder: '#8A6040',
  },

  // ── Falta ───────────────────────────────────────────────────────────────────
  noshow: {
    bg:           '#FFF0F3',
    timeColor:    '#C8607A',
    borderColor:  '#B82050',
    textColor:    '#B82050',
    legendBg:     '#FFF0F3',
    legendBorder: '#B82050',
  },

  // ── Bloqueado — leve: quase branco, contorno tracejado e hachura suave ──────
  // O que o separa do Agendado é o contorno tracejado e o cadeado, não um fundo
  // fechado (pedido 2026-11-03). O "Blocked Time" (horário bloqueado da empresa)
  // continua com as listras suaves sobre fundo transparente.
  blocked: {
    bg:           '#FBFCFD',
    bgImage:      BLOCK_STRIPE_PATTERN,
    timeColor:    '#8896A8',
    borderColor:  '#8A96A8',
    textColor:    '#64748B',
    legendBg:     '#FBFCFD',
    legendBorder: '#A0AABB',
  },
};

// Monta uma cor da paleta no mesmo formato das entradas de status
const paletteEntry = (label, base, extra = {}) => ({
  label,
  bg:           base.bg,
  timeColor:    base.timeColor,
  borderColor:  base.borderColor,
  textColor:    base.textColor,
  legendBg:     base.bg,
  legendBorder: base.borderColor,
  ...extra,
});

/**
 * Paleta curada para as cores da empresa. As chaves batem com
 * AppointmentSteps::COLORS (PHP). As primeiras são as cores padrão dos status,
 * para a pessoa poder voltar a elas ou usá-las em outro status.
 * cardText: cor do nome no card quando o fundo é escuro.
 */
export const PALETTE = {
  slate:  paletteEntry('Cinza', AppointmentStatusColors.scheduled),
  sky:    paletteEntry('Azul claro', AppointmentStatusColors.confirmed),
  blue:   paletteEntry('Azul', AppointmentStatusColors.waiting),
  indigo: paletteEntry('Azul forte', AppointmentStatusColors.doing, { cardText: '#FFFFFF' }),
  teal:   paletteEntry('Verde-água', { bg: '#E3F5F2', timeColor: '#4A9189', borderColor: '#0F766E', textColor: '#0F766E' }),
  green:  paletteEntry('Verde', AppointmentStatusColors.completed),
  amber:  paletteEntry('Amarelo', { bg: '#FFF5D6', timeColor: '#A07B22', borderColor: '#8A6100', textColor: '#8A6100' }),
  orange: paletteEntry('Laranja', { bg: '#FFEBDD', timeColor: '#B8693A', borderColor: '#B4500F', textColor: '#B4500F' }),
  brown:  paletteEntry('Marrom', AppointmentStatusColors.canceled),
  rose:   paletteEntry('Vermelho', AppointmentStatusColors.noshow),
  pink:   paletteEntry('Rosa', { bg: '#FCEBF5', timeColor: '#B05C93', borderColor: '#A3247A', textColor: '#A3247A' }),
  purple: paletteEntry('Roxo', { bg: '#F0EBFF', timeColor: '#836CC0', borderColor: '#5B3CC4', textColor: '#5B3CC4' }),
};

/** Cor padrão de cada status que a empresa pode trocar (AppointmentSteps::COLOR_STATUSES) */
export const DEFAULT_STATUS_PALETTE = {
  scheduled: 'slate',
  confirmed: 'sky',
  waiting:   'blue',
  doing:     'indigo',
  completed: 'green',
  canceled:  'brown',
  noshow:    'rose',
};

// Variações que seguem a cor escolhida para o status principal
const OVERRIDE_ALIASES = { confirmed_by_patient: 'confirmed', canceled_by_patient: 'canceled' };

let statusOverrides = {};

/** Cores escolhidas pela empresa: { status: chave da paleta } (DataProvider, ao carregar a empresa) */
export function setStatusColorOverrides(map) {
  statusOverrides = map && typeof map === 'object' && !Array.isArray(map) ? { ...map } : {};
}

/** Chave da paleta escolhida para o status (null = cor padrão) */
export function getStatusOverride(statusId) {
  const key = statusOverrides[OVERRIDE_ALIASES[statusId] || statusId];
  return PALETTE[key] ? key : null;
}

/** Cores de uma chave da paleta (null quando a chave não existe) */
export function getPaletteColors(key) {
  return PALETTE[key] || null;
}

/**
 * Retorna as cores de um status pelo seu ID string, já com a cor escolhida pela empresa.
 * Retorna as cores de "scheduled" como fallback se o status não for encontrado.
 *
 * @param {string} statusId - ID do status (ex: 'confirmed', 'waiting', 'blocked')
 * @returns {object} Objeto com todas as propriedades de cor do status
 */
export function getStatusColors(statusId) {
  const override = getStatusOverride(statusId);
  if (override) {
    // Só as cores: o nome da paleta ("Amarelo") não pode sobrescrever o nome do status
    const { label, ...colors } = PALETTE[override];
    return colors;
  }
  return AppointmentStatusColors[statusId] || AppointmentStatusColors.scheduled;
}

/**
 * Retorna os itens da legenda prontos para renderização.
 * Cada item contém: { label, bg, border }
 *
 * @returns {Array<{label: string, bg: string, border: string}>}
 */
export function getLegendItems() {
  const legend = (statusId) => getStatusColors(statusId);
  return [
    { label: 'Agendado',                bg: legend('scheduled').legendBg,           border: legend('scheduled').legendBorder },
    { label: 'Reagendado',              bg: legend('rescheduled').legendBg,          border: legend('rescheduled').legendBorder },
    { label: 'Confirmado',              bg: legend('confirmed').legendBg,            border: legend('confirmed').legendBorder },
    { label: 'Conf. pelo paciente',     bg: legend('confirmed_by_patient').legendBg, border: legend('confirmed_by_patient').legendBorder },
    { label: 'Aguardando no local',     bg: legend('waiting').legendBg,              border: legend('waiting').legendBorder },
    { label: 'Em atendimento',          bg: legend('doing').legendBg,                border: legend('doing').legendBorder },
    { label: 'Realizado',               bg: legend('completed').legendBg,            border: legend('completed').legendBorder },
    { label: 'Cancelado',               bg: legend('canceled').legendBg,             border: legend('canceled').legendBorder },
    { label: 'Cancelado pelo paciente', bg: legend('canceled_by_patient').legendBg,  border: legend('canceled_by_patient').legendBorder },
    { label: 'Falta',                   bg: legend('noshow').legendBg,               border: legend('noshow').legendBorder },
    { label: 'Bloqueado',               bg: legend('blocked').legendBg,              border: legend('blocked').legendBorder },
  ];
}

export default AppointmentStatusColors;
